import { WBBox, WBButton, WBFlex, WBTextFieldV2 } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { Template, TemplateType } from '@admiin-com/ds-graphql';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { DocumentEditor } from '../../components/DocumentEditor';
import { useState } from 'react';
import { BillCreateFormData } from '../../providers/TaskCreationProvider/TaskCreationProvider';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';

export interface DocumentCreateFormProps {
  defaultTemplate?: Template | null;
  loading: boolean;
  onSubmit: (data: TemplateFormData) => Promise<void>;
  taxCreate?: boolean;
  taskCreationContext?: BillCreateFormData;
}

type TemplateFormData = {
  title: string;
  templateJson?: string;
  templateHtml?: string;
  includeLetterhead?: boolean;
};

export function DocumentCreateForm({
  loading,
  onSubmit,
  defaultTemplate,
  taxCreate,
  taskCreationContext,
}: DocumentCreateFormProps) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TemplateFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: defaultTemplate?.title || '',
      templateJson: defaultTemplate?.templateJson || undefined,
      templateHtml: defaultTemplate?.templateHtml || undefined,
      includeLetterhead: (defaultTemplate as any)?.includeLetterhead || true,
    },
  });
  const templateHtml = useWatch({ control, name: 'templateHtml' });
  const templateJson = useWatch({ control, name: 'templateJson' });
  const includeLetterhead = useWatch({ control, name: 'includeLetterhead' });
  const templateType = defaultTemplate?.type;

  const isEditorEmpty = (jsonString?: string) => {
    if (!jsonString) return true;

    try {
      const state = JSON.parse(jsonString);
      const root = state.root;

      // Check if root has only one child
      if (!root || !root.children || root.children.length !== 1) {
        return false;
      }

      // Check if the single child is an empty paragraph
      const firstChild = root.children[0];
      return (
        firstChild.type === 'paragraph' &&
        (!firstChild.children || firstChild.children.length === 0)
      );
    } catch (e) {
      return true;
    }
  };

  const handleFormSubmit = async (data: TemplateFormData) => {
    await onSubmit(data);
  };

  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  return (
    <WBBox
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{
        overflow: 'auto',
      }}
    >
      <Controller
        control={control}
        name="title"
        rules={{
          required: t('documentNameRequired', { ns: 'documents' }),
          minLength: {
            value: 1,
            message: t('documentNameRequired', { ns: 'documents' }),
          },
        }}
        defaultValue=""
        disabled={taxCreate}
        render={({ field }) => (
          <WBTextFieldV2
            {...field}
            label={t('documentName', { ns: 'documents' })}
            placeholder={t('documentNamePlaceholder', { ns: 'documents' })}
            error={!!errors.title}
            helperText={errors.title?.message}
            margin="dense"
            fullWidth
            autoFocus
          />
        )}
      />

      <WBBox sx={{ mt: 3 }}>
        <Controller
          control={control}
          name="templateJson"
          render={({ field }) => (
            <DocumentEditor
              onChange={(
                jsonString: string,
                html: string,
                includeLetterheadValue: boolean
              ) => {
                field.onChange(jsonString);
                setValue('templateHtml', html);
                setValue('includeLetterhead', includeLetterheadValue);
              }}
              initialContent={field.value}
              placeholder="Start typing your document..."
              includeLetterhead={includeLetterhead}
              templateType={templateType}
              onLetterheadChange={(includeLetterheadValue: boolean) => {
                setValue('includeLetterhead', includeLetterheadValue);
              }}
            />
          )}
        />
      </WBBox>

      <WBFlex sx={{ mt: 5 }} gap={2}>
        <WBButton
          type="button"
          fullWidth
          variant="outlined"
          onClick={() => setPreviewModalOpen(true)}
          disabled={isEditorEmpty(templateJson)}
        >
          {t('preview', {
            ns: 'templates',
            defaultValue: 'Preview',
          })}
        </WBButton>
        <WBButton type="submit" loading={loading} fullWidth disabled={loading}>
          {t(
            taxCreate ? 'Confirm' : defaultTemplate?.id ? 'update' : 'create',
            { ns: 'common' }
          )}
        </WBButton>
      </WBFlex>
      <DocumentPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        templateHtml={templateHtml}
        taskCreationContext={taskCreationContext}
        includeLetterhead={includeLetterhead}
        templateType={templateType}
      />
    </WBBox>
  );
}

export default DocumentCreateForm;
